import React, {useState, useRef, useMemo} from 'react';
import {Box} from '@spothero/ui';
import ReactDatePicker from 'react-datepicker';
import {PropTypes} from 'prop-types';
import reactDatePickerStyleOverrides from './DatePickerStyleOverrides';
import DatePickerInput from './DatePickerInput';
import DatePickerHeader from './DatePickerHeader';
import {formatDateForURL} from '../../utils/format-utils';
import {DATE_API_V2} from '../../../common/constants';
import dayjs from 'dayjs';

function DatePicker({
    disabled = false,
    labelText,
    setSelectedDate,
    selectedDate,
    minDate,
}) {
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    /* 
    onClickOutside prop set in react-datepicker will close the date picker. 
    If input is clicked when datepicker is open, there is a flicker since the outside click closes the datepicker and input click open it again
    To avoid this behavior ref is created and event target id and ref id is compared and avoid outside click handler closing the datepicker
    */
    const datePickerInputRef = useRef();
    const convertedDate = useMemo(() => formatDateForURL(selectedDate), [
        selectedDate,
    ]);
    const handleDone = () => {
        setIsDatePickerOpen(false);
    };

    const handleOutsideClick = event => {
        if (event.target.id !== datePickerInputRef.current.id) {
            handleDone();
        }
    };

    const handleInputClick = () => {
        if (!disabled) {
            setIsDatePickerOpen(true);
        }
    };
    const handleChange = currentDate => {
        setSelectedDate(currentDate);
        setIsDatePickerOpen(false);
    };

    return (
        <Box position="relative" width="50%" maxWidth={80}>
            <DatePickerInput
                openDatePicker={handleInputClick}
                selectedDate={convertedDate}
                disabled={disabled}
                labelText={labelText}
                ref={datePickerInputRef}
            />
            {isDatePickerOpen && (
                <Box
                    position="fixed"
                    zIndex="layer4"
                    width={{tablet: '400px', desktop: '400px'}}
                    mr={{base: '4'}}
                    sx={reactDatePickerStyleOverrides}
                    data-testid="DatePicker-popper"
                >
                    <ReactDatePicker
                        disabled={disabled}
                        shouldCloseOnSelect
                        showPopperArrow={false}
                        onChange={handleChange}
                        onClickOutside={handleOutsideClick}
                        formatWeekDay={nameOfDay => nameOfDay.substr(0, 1)}
                        minDate={minDate}
                        renderCustomHeader={props => (
                            <DatePickerHeader {...props} />
                        )}
                        inline
                        selected={dayjs(selectedDate).format(DATE_API_V2)}
                    />
                </Box>
            )}
        </Box>
    );
}

DatePicker.propTypes = {
    setSelectedDate: PropTypes.func,
    selectedDate: PropTypes.object,
    pickerProps: PropTypes.object,
    disabled: PropTypes.bool,
    inputStyleProps: PropTypes.object,
    labelText: PropTypes.string,
    id: PropTypes.string,
    ref: PropTypes.object,
    minDate: PropTypes.object,
};

export default DatePicker;
